// src/hooks/useTemplates.js
import { useState, useEffect } from 'react';
import infografiaService from '../helpers/infografia/infografiaService';

/**
 * Hook para cargar templates con fallback a templates hardcodeados
 * @param {object} filters - Filtros para templates (categoria, esPublico, etc.)
 * @returns {object} { templates, loading, error, refetch }
 */
export const useTemplates = (filters = {}) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [useHardcoded, setUseHardcoded] = useState(false);

    // Templates hardcodeados como fallback
    const FALLBACK_TEMPLATES = [
        {
            id: 'feedback',
            // NO incluir _id para templates hardcodeados (lo distingue de backend)
            nombre: 'Feedback/Valoración',
            descripcion: 'Cartel para solicitar opiniones de clientes con escala de satisfacción',
            categoria: 'feedback',
            icono: 'FaStar',
            dimensiones: { ancho: 2480, alto: 3508, unidad: 'px', dpi: 300 },
            elementos: [],
            fondo: { tipo: 'color', valor: { color: '#FFFFFF' } }
        },
        {
            id: 'promocional',
            nombre: 'Promocional Club',
            descripcion: 'Cartel promocional para programa de fidelización con beneficios y QR',
            categoria: 'promocional',
            icono: 'FaGift',
            dimensiones: { ancho: 2480, alto: 3508, unidad: 'px', dpi: 300 },
            elementos: [],
            fondo: { tipo: 'gradiente', valor: { direccion: '135deg', colores: [{ color: '#3B82F6', posicion: 0 }, { color: '#1D4ED8', posicion: 100 }] } }
        },
        {
            id: 'reviews',
            nombre: 'Reviews/Reseñas',
            descripcion: 'Cartel simple para solicitar reseñas en Google o redes sociales',
            categoria: 'reviews',
            icono: 'FaComment',
            dimensiones: { ancho: 1080, alto: 1080, unidad: 'px', dpi: 300 },
            elementos: [],
            fondo: { tipo: 'color', valor: { color: '#FFFFFF' } }
        },
        {
            id: 'custom',
            nombre: 'Personalizado',
            descripcion: 'Crea tu diseño desde cero con total libertad',
            categoria: 'custom',
            icono: 'FaPaintBrush',
            dimensiones: { ancho: 2480, alto: 3508, unidad: 'px', dpi: 300 },
            elementos: [],
            fondo: { tipo: 'color', valor: { color: '#FFFFFF' } }
        }
    ];

    const loadTemplates = async () => {
        try {
            setLoading(true);
            const response = await infografiaService.getTemplates({
                esPublico: true,
                ...filters
            });
            
            if (response.success && response.templates && response.templates.length > 0) {
                setTemplates(response.templates);
                setUseHardcoded(false);
                setError(null);
            } else {
                // Si no hay templates en backend, usar hardcodeados
                setTemplates(FALLBACK_TEMPLATES);
                setUseHardcoded(true);
            }
        } catch (err) {
            // Usar templates hardcodeados como fallback
            setTemplates(FALLBACK_TEMPLATES);
            setUseHardcoded(true);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTemplates();
    }, [JSON.stringify(filters)]);

    return {
        templates,
        loading,
        error,
        useHardcoded,
        refetch: loadTemplates
    };
};

/**
 * Hook para cargar dimensiones estándar
 * @returns {object} { dimensiones, loading, error, dimensionesPorCategoria }
 */
export const useDimensionesEstandar = () => {
    const [dimensiones, setDimensiones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadDimensiones = async () => {
            try {
                setLoading(true);
                const response = await infografiaService.getDimensionesEstandar();
                
                if (response.success && response.dimensiones) {
                    setDimensiones(response.dimensiones);
                    setError(null);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadDimensiones();
    }, []);

    // Agrupar dimensiones por categoría
    const dimensionesPorCategoria = dimensiones.reduce((acc, dim) => {
        if (!acc[dim.categoria]) {
            acc[dim.categoria] = [];
        }
        acc[dim.categoria].push(dim);
        return acc;
    }, {});

    return {
        dimensiones,
        loading,
        error,
        dimensionesPorCategoria
    };
};


